'use client'

import * as React from 'react'
import { LuCheck, LuChevronDown } from 'react-icons/lu'
import { TbGrain } from 'react-icons/tb'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { cn } from '@/lib/utils'

export type GranularityValue = 'hourly' | 'daily' | 'monthly'

interface GranularityOption {
  value: GranularityValue
  label: string
}

const granularityOptions: GranularityOption[] = [
  { value: 'hourly', label: 'Hourly' },
  { value: 'daily', label: 'Daily' },
  { value: 'monthly', label: 'Monthly' },
]

interface GranularitySelectorProps {
  value?: GranularityValue
  onChange?: (value: GranularityValue) => void
  /** Number of days in the selected date range */
  dateRangeDays?: number
  className?: string
}

const GranularitySelector = ({
  value: externalValue,
  onChange: externalOnChange,
  dateRangeDays = 7,
  className,
}: GranularitySelectorProps) => {
  const [open, setOpen] = React.useState(false)
  
  // Internal state for uncontrolled mode
  const [internalValue, setInternalValue] = React.useState<GranularityValue>('daily')
  
  // Use external value if provided, otherwise use internal state
  const value = externalValue !== undefined ? externalValue : internalValue
  const onChange = externalOnChange || setInternalValue

  // Check if granularity options are allowed based on date range
  const isDailyAllowed = dateRangeDays <= 90
  const isHourlyAllowed = dateRangeDays <= 14

  // Get display text for the button
  const getDisplayText = () => {
    const option = granularityOptions.find(opt => opt.value === value)
    return option?.label || 'Select'
  }

  // Handle option selection
  const handleSelect = (optionValue: GranularityValue) => {
    if (optionValue === 'daily' && !isDailyAllowed) {
      toast.error('Daily granularity is only available for date ranges of 90 days or less.')
      return
    }

    if (optionValue === 'hourly' && !isHourlyAllowed) {
      toast.error('Hourly granularity is only available for date ranges of 14 days or less.')
      return
    }
    
    onChange(optionValue)
    setOpen(false)
  }

  // If currently selected value becomes invalid, switch to an allowed one
  React.useEffect(() => {
    if (value === 'hourly' && !isHourlyAllowed) {
      if (isDailyAllowed) {
        onChange('daily')
      } else {
        onChange('monthly')
      }
    } else if (value === 'daily' && !isDailyAllowed) {
      onChange('monthly')
    }
  }, [dateRangeDays, value, isDailyAllowed, isHourlyAllowed, onChange])

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="transparent-outline"
          size="xs"
          className={cn(
            'hover:bg-[#F2EEFF] hover:text-[#654EDA] hover:border-[#E2DAFF]',
            'focus-visible:ring-[#654EDA]/20 focus-visible:border-[#654EDA] focus-visible:ring-2',
            open && 'bg-[#E2DAFF] text-[#654EDA] border-[#C6B9FF] hover:bg-[#E2DAFF] focus-visible:ring-0',
            className
          )}
        >
          <TbGrain className="size-4" />
          {getDisplayText()}
          <LuChevronDown
            className={cn(
              'size-3.5 ml-0.5 transition-transform duration-200',
              open && 'rotate-180'
            )}
          />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[180px] p-0 border-[#EDEEF3] shadow-lg"
        align="start"
        sideOffset={6}
      >
        {/* Header */}
        <div className="px-3 py-2 border-b border-[#EDEEF3]">
          <span className="text-[11px] font-medium text-[#8A8F98] uppercase tracking-wide">
            Granularity
          </span>
        </div>

        {/* Options */}
        <div className="py-1">
          {granularityOptions.map((option) => {
            const isSelected = value === option.value
            const isDisabled = 
              (option.value === 'daily' && !isDailyAllowed) || 
              (option.value === 'hourly' && !isHourlyAllowed)
            
            const maxDays = option.value === 'daily' ? 90 : option.value === 'hourly' ? 14 : null

            return (
              <div
                key={option.value}
                className={cn(
                  'flex items-center gap-3 px-3 h-[32px] cursor-pointer transition-colors',
                  isDisabled 
                    ? 'cursor-not-allowed opacity-50' 
                    : 'hover:bg-[#F7F8F8]',
                  isSelected && !isDisabled && 'bg-[#F7F8F8]'
                )}
                onClick={() => handleSelect(option.value)}
              >
                <span className={cn(
                  'text-[13px] font-medium flex-1',
                  isSelected ? 'text-[#654EDA]' : 'text-[#08090A]',
                  isDisabled && 'text-[#8A8F98]'
                )}>
                  {option.label}
                </span>
                {isDisabled && maxDays && (
                  <span className="text-[10px] text-[#8A8F98] bg-[#EDEEF3] px-1.5 py-0.5 rounded">
                    Max {maxDays} days
                  </span>
                )}
                {isSelected && !isDisabled && (
                  <LuCheck className="size-4 text-[#654EDA] shrink-0" />
                )}
              </div>
            )
          })}
        </div>
      </PopoverContent>
    </Popover>
  )
}

export { GranularitySelector }

